<template>
    <div>

        <div
            v-if="isbjDown"
            class="kgr-alert kgr-hidden-print kgr-alert--error isbj-down"
        >
            {{ $t('Das ISBJ-Trägerportal antwortet aktuell nicht. Sobald es wieder erreichbar ist stellen wir die Anfrage erneut.') }}
        </div>
        <div class="kgr-inputGroup">
            <div class="kgr-inputGroup__label">
                {{ $t('Status:') }}
            </div>
            <kgr-checkbox-group
                label-prefix="IsbjStatusPicker.checkbox"
                inline
                :value="statusFilterValues"
                :options="statusFilterOptions"
            />
        </div>
        <div class="kgr-inputGroup">
            <div class="kgr-inputGroup__label">
                {{ $t('Vertragsstatus:') }}
            </div>
            <kgr-checkbox-group
                label-prefix="IsbjContractStatusPicker.checkbox"
                inline
                :value="contractStatusFilterValues"
                :options="contractStatusFilterOptions"
            />
        </div>
        <div
            class="kgr-inputGroup"
            style="display: flex"
        >
            <kgr-section-select
                v-model="section"
                :federation-id="federation"
                default-text="Alle Bereiche"
                inline
            />
            <kgr-kindergarten-select
                v-model="kindergarten"
                :federation-id="federation"
                :section="section"
                :province="berlin"
                default-text="Alle Einrichtungen"
            />
            <kgr-number-input
                v-model="year"
                :label="$t('Jahr')"
                inline
                integer-only
                class="w-32"
            />
        </div>
        <kgr-button
            :value="$t('anzeigen')"
            class="mb1"
            :pending="loading"
            @click="load()"
        />
        <kgr-button
            v-if="hasLoaded"
            :value="$t('Alle synchronisieren')"
            class="mb1 ml1"
            :pending="syncingAll"
            :disabled="loading || rows.length === 0"
            @click="syncAll()"
        />
        <kgr-empty-data
            v-if="!hasLoaded"
            :value="$t('Klicken Sie auf anzeigen, um die Daten zu laden.')"
        />
        <div
            v-else
            class="relative"
        >
            <kgr-loading
                v-if="loading"
                class="absolute inset-0 bg-white/80 z-10 flex items-center justify-center"
            />
            <table
                vue:is="kgr-table"
                class="kgr-table--block"
                :order="[[1, 'asc'], [2, 'asc']]"
            >
                <thead>
                    <tr class="kgr-table__row">
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                        >
                            {{ $t('Status') }}
                        </th>
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                            sortable
                        >
                            {{ $t('Nachname') }}
                        </th>
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                            sortable
                        >
                            {{ $t('Vorname') }}
                        </th>
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                            sortable
                        >
                            {{ $t('Einrichtung') }}
                        </th>
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                        >
                            {{ $t('Gutschein') }}
                        </th>
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                        >
                            {{ $t('Vertragsnummer') }}
                        </th>
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                        >
                            {{ $t('Start') }}
                        </th>
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                        >
                            {{ $t('Ende') }}
                        </th>
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                        >
                            {{ $t('Betreuungsumfang') }}
                        </th>
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline"
                        >
                            {{ $t('Anmerkungen') }}
                        </th>
                        <th
                            vue:is="kgr-th"
                            class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions"
                        ></th>
                    </tr>
                </thead>
                <tbody>
                    <template
                        v-for="row in rows"
                        :key="row.type === 'mismatch' ? 'mismatch-' + row.child.id : row.id"
                    >
                        <kgr-isbj-mismatch-row
                            v-if="row.type === 'mismatch'"
                            :child="row.child"
                        />
                        <kgr-isbj-contract-row
                            v-else-if="row.type === 'contract'"
                            :status="row.status"
                            :child="row.child"
                            :subvention="row.subvention"
                            :contract="row.contract"
                            :federation-id="federation"
                            @reload="load()"
                        />
                        <kgr-isbj-regular-time-row
                            v-else-if="row.type === 'regularTime'"
                            :status="row.status"
                            :child="row.child"
                            :subvention="row.subvention"
                            :regular-time="row.regularTime"
                            :related-contract="row.relatedContract"
                            :federation-id="federation"
                            @reload="load()"
                        />
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { BERLIN } from '../../../js/constants/province'
import CheckboxGroup from '../../common/form/checkbox/CheckboxGroup';
import EmptyData from '../../common/EmptyData';
import IsbjChildRepository from '../../../js/repository/IsbjChildRepository';
import IsbjContractRow from './IsbjContractRow';
import IsbjMismatchRow from './IsbjMismatchRow';
import IsbjRegularTimeRow from './IsbjRegularTimeRow';
import KgrButton from '../../common/form/button/KgrButton';
import KindergartenSelect from '../../common/form/select/KindergartenSelect';
import Loading from '../../common/Loading';
import NumberInput from '../../common/form/input/NumberInput.vue'
import SectionSelect from '../../common/form/select/SectionSelect'

export default {
    components: {
        'kgr-empty-data': EmptyData,
        'kgr-loading': Loading,
        'kgr-button': KgrButton,
        'kgr-isbj-contract-row': IsbjContractRow,
        'kgr-isbj-mismatch-row': IsbjMismatchRow,
        'kgr-isbj-regular-time-row': IsbjRegularTimeRow,
        'kgr-checkbox-group': CheckboxGroup,
        'kgr-kindergarten-select': KindergartenSelect,
        'kgr-section-select': SectionSelect,
        'kgr-number-input': NumberInput,
    },
    props: {
        federation: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            hasLoaded: false,
            loading: false,
            syncingAll: false,
            isbjDown: false,
            section: null,
            kindergarten: null,
            contracts: [],
            regularTimes: [],
            statusFilterOptions: ['not_synced', 'pending_sync', 'sync_error', 'out_of_sync', 'in_sync'],
            statusFilterValues: ['not_synced', 'pending_sync', 'sync_error', 'out_of_sync', 'in_sync'],
            contractStatusFilterOptions: ['created', 'signed_up', 'on_waiting_list', 'waiting_for_contract_signing', 'waiting_for_contract_start', 'active', 'archived'],
            contractStatusFilterValues: ['created', 'signed_up', 'on_waiting_list', 'waiting_for_contract_signing', 'waiting_for_contract_start', 'active', 'archived'],
            year: new Date().getFullYear(),
        }
    },
    computed: {
        berlin() {
            return BERLIN;
        },
        rows() {
            const contracts = this.contracts.map(contract => {
                contract.type = 'contract';
                return contract;
            });
            const regularTimes = this.regularTimes.map(regularTime => {
                regularTime.type = 'regularTime';
                return regularTime;
            });
            const rows = contracts.concat(regularTimes);

            // Filter out rows for children with date mismatch
            const normalRows = rows.filter(row => !row.child.hasDateMismatch);

            // Create one warning row per child with date mismatch
            const mismatchedChildren = {};
            for (const row of rows) {
                if (row.child.hasDateMismatch) {
                    mismatchedChildren[row.child.id] = { type: 'mismatch', child: row.child };
                }
            }
            const warningRows = Object.values(mismatchedChildren);

            // Combine and sort together by child name
            const allRows = normalRows.concat(warningRows);
            allRows.sort((a, b) => {
                const nameA = a.child.name.lastName + ', ' + a.child.name.firstName;
                const nameB = b.child.name.lastName + ', ' + b.child.name.firstName;
                return nameA.localeCompare(nameB);
            });

            return allRows;
        },
    },
    watch: {
        section(section) {
            window.localStorage.setItem('kgr_isbj_filter_section', JSON.stringify(section));
        },
        kindergarten(kindergarten) {
            window.localStorage.setItem('kgr_isbj_filter_kindergarten', JSON.stringify(kindergarten));
        },
        statusFilterValues(statusFilterValues) {
            window.localStorage.setItem('kgr_isbj_filter_status', JSON.stringify(statusFilterValues));
        },
        contractStatusFilterValues(contractStatusFilterValues) {
            window.localStorage.setItem('kgr_isbj_filter_contract_status', JSON.stringify(contractStatusFilterValues));
        },
    },
    created() {
        let filterSection = window.localStorage.getItem('kgr_isbj_filter_section');
        if (filterSection) {
            filterSection = JSON.parse(filterSection);
            this.section = filterSection;
        }
        let filterKindergarten = window.localStorage.getItem('kgr_isbj_filter_kindergarten');
        if (filterKindergarten) {
            filterKindergarten = JSON.parse(filterKindergarten);
            this.kindergarten = filterKindergarten;
        }
        let filterStatus = window.localStorage.getItem('kgr_isbj_filter_status');
        if (filterStatus) {
            filterStatus = JSON.parse(filterStatus);
            this.statusFilterValues = filterStatus;
        }
        let filterContractStatus = window.localStorage.getItem('kgr_isbj_filter_contract_status');
        if (filterContractStatus) {
            filterContractStatus = JSON.parse(filterContractStatus);
            this.contractStatusFilterValues = filterContractStatus;
        }
    },
    methods: {
        syncAll() {
            const childIds = [...new Set(this.rows.map(row => row.child.id))];
            if (childIds.length === 0) {
                return;
            }

            this.syncingAll = true;
            IsbjChildRepository
                .bulkUpdate(this.federation, childIds)
                .then(() => {
                    this.load();
                })
                .catch(() => {
                    this.$notify.error('Es ist ein Fehler aufgetreten. Bitte laden Sie die Seite neu.');
                })
                .finally(() => this.syncingAll = false);
        },
        load() {
            this.loading = true;
            const contracts = new Promise((resolve, reject) => {
                IsbjChildRepository
                    .findContracts(this.federation, this.section, this.kindergarten, this.statusFilterValues, this.contractStatusFilterValues, this.year)
                    .then(data => resolve(data))
                    .catch(error => reject(error))
                ;
            });
            const regularTimes = new Promise((resolve, reject) => {
                IsbjChildRepository
                    .findRegularTimes(this.federation, this.section, this.kindergarten, this.statusFilterValues, this.contractStatusFilterValues, this.year)
                    .then(data => resolve(data))
                    .catch(error => reject(error))
                ;
            });
            Promise
                .all([contracts, regularTimes])
                .then(([dataContracts, dataRegularTimes]) => {
                    this.contracts = dataContracts;
                    this.regularTimes = dataRegularTimes;
                    this.loading = false;
                    this.hasLoaded = true;
                    this.isbjDown = false;
                })
                .catch(() => {
                    this.isbjDown = true;
                    this.loading = false;
                })
            ;
        },
    },
}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
.diff {
    background-color: #f9f6c1;
}
.isbj-down {
    margin-bottom: 14px;
}
</style>
